import axios from "axios";
import { toast } from "sonner";
import { money } from "@/data/products";
import { promoLabel, urlB64ToUint8Array } from "@/components/admin/shared";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
// Admin auth now rides on an httpOnly cookie set by /auth/login.
const authHeader = () => ({ withCredentials: true });

// All imperative admin handlers, extracted from Admin.jsx. `s` is the state bag.
export function useAdminActions(s) {
  const {
    loadAll, refreshCatalog, PRODUCTS, stock, promos, subs, ambForm, blast, blastSchedule,
    qtyDraft, costDraft, trackDraft, prodDraft, newProd, newPromo, dealDraft,
    winEmail, resetConfirm, goalDraft, mktKind, mktTopic, autoWinback, installPrompt, pushKey,
    email, password,
    setInstallPrompt, setPushOn, setMonthlyGoal, setResetConfirm, setAuthed, setOrders,
    setStock, setCosts, setNewProd, setShowNewProd, setEditProd, setProdDraft, setMktOut,
    setMktBusy, setWinAllBusy, setWinAllOut, setWinBusy, setWinOut, setAutoWinback,
    setBlastBusy, setBlast, setBlastSchedule, setNewPromo, setDealDraft, setCarts,
    setDigestHour, setPayingId,
  } = s;

  const installApp = async () => {
    if (!installPrompt) { toast.info("On iPhone: tap Share → Add to Home Screen. On Android: use the browser menu → Install."); return; }
    installPrompt.prompt();
    await installPrompt.userChoice;
    setInstallPrompt(null);
  };

  const enablePush = async () => {
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) { toast.error("Push not supported on this device"); return; }
    if (!pushKey) { toast.error("Push is not configured"); return; }
    try {
      const perm = await Notification.requestPermission();
      if (perm !== "granted") { toast.error("Notifications permission denied"); return; }
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlB64ToUint8Array(pushKey) });
      await axios.post(`${API}/push/subscribe`, sub.toJSON(), authHeader());
      setPushOn(true);
      toast.success("Order alerts enabled on this device");
      await axios.post(`${API}/push/test`, {}, authHeader()).catch((err) => console.warn("push test failed", err));
    } catch (e) { toast.error("Could not enable notifications"); }
  };

  const disablePush = async () => {
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) { await axios.post(`${API}/push/unsubscribe`, sub.toJSON(), authHeader()); await sub.unsubscribe(); }
      setPushOn(false);
      toast.success("Order alerts disabled");
    } catch { toast.error("Could not disable notifications"); }
  };

  const saveGoal = async () => {
    const g = parseInt(goalDraft, 10);
    if (isNaN(g) || g < 0) { toast.error("Enter a valid goal"); return; }
    try { await axios.patch(`${API}/settings/goal`, { monthly_goal: g }, authHeader()); setMonthlyGoal(g); toast.success(`Monthly goal set to $${g.toLocaleString()}`); }
    catch { toast.error("Failed to save goal"); }
  };

  const resetData = async () => {
    if (resetConfirm !== "RESET") { toast.error('Type RESET to confirm'); return; }
    try {
      const { data } = await axios.post(`${API}/admin/reset-data`, { confirm: "RESET" }, authHeader());
      const total = Object.values(data.wiped).reduce((a, b) => a + b, 0);
      toast.success(`Business data reset — ${total} records cleared`);
      setResetConfirm("");
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Reset failed"); }
  };

  const login = async () => {
    try {
      await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
      setAuthed(true);
      toast.success("Welcome back");
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Invalid email or password");
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/orders/${id}/status`, { status }, authHeader());
      setOrders((prev) => prev.map((o) => (o.id === id
        ? { ...o, status, status_history: [...(o.status_history || []), { status, at: new Date().toISOString() }] } : o)));
      toast.success(`Order marked ${status}`);
    } catch { toast.error("Failed to update status"); }
  };

  const notifyRestock = async (product_id, product_name) => {
    try {
      const { data } = await axios.post(`${API}/restock-requests/notify`, { product_id, product_name }, authHeader());
      toast.success(`Emailed ${data.notified} waiting customer(s)`);
      loadAll();
    } catch { toast.error("Failed to notify"); }
  };

  const toggleStock = async (p) => {
    const soldOut = p.id in stock ? stock[p.id].sold_out : !!p.soldOut;
    const next = !soldOut;
    try {
      const { data } = await axios.patch(`${API}/product-status/${p.id}`, { sold_out: next, product_name: p.name }, authHeader());
      const effective = data.sold_out;
      setStock((prev) => ({ ...prev, [p.id]: { sold_out: effective, quantity: data.quantity } }));
      if (!next && effective) {
        toast.info(`${p.name} stays sold out — its quantity is 0. Set a quantity to restock.`);
      } else if (!effective && data.notified > 0) {
        toast.success(`${p.name} back in stock — emailed ${data.notified} waiting customer(s)`);
      } else {
        toast.success(`${p.name} marked ${effective ? "sold out" : "in stock"}`);
      }
      if (!effective) loadAll();
    } catch { toast.error("Failed to update stock"); }
  };

  const saveQuantity = async (p) => {
    const q = parseInt(qtyDraft[p.id], 10);
    if (isNaN(q) || q < 0) { toast.error("Enter a valid quantity"); return; }
    try {
      const { data } = await axios.patch(`${API}/product-status/${p.id}`, { quantity: q, product_name: p.name }, authHeader());
      setStock((prev) => ({ ...prev, [p.id]: { sold_out: data.sold_out, quantity: data.quantity } }));
      if (data.notified > 0) toast.success(`${p.name} restocked to ${q} — auto-emailed ${data.notified} waiting customer(s)`, { duration: 6000 });
      else toast.success(`${p.name} stock set to ${q}`);
      loadAll();
    } catch { toast.error("Failed to save quantity"); }
  };

  const saveCost = async (p) => {
    const val = parseFloat(costDraft[p.id]);
    if (isNaN(val) || val < 0) { toast.error("Enter a valid cost"); return; }
    try {
      await axios.patch(`${API}/product-status/${p.id}`, { cost: val, product_name: p.name }, authHeader());
      setCosts((prev) => ({ ...prev, [p.id]: val }));
      toast.success(`${p.name} cost set to $${val}`);
    } catch { toast.error("Failed to save cost"); }
  };

  const createProduct = async () => {
    const price = parseFloat(newProd.price);
    if (!newProd.name.trim()) { toast.error("Product name is required"); return; }
    if (!newProd.size.trim()) { toast.error("Dosage / size is required (e.g. 10 mg)"); return; }
    if (isNaN(price) || price < 0) { toast.error("Enter a valid price"); return; }
    try {
      await axios.post(`${API}/products`, { ...newProd, price }, authHeader());
      toast.success(`${newProd.name} added to catalog`);
      setNewProd({ name: "", size: "", price: "", blurb: "", image: "white", bestseller: false, cas: "", halfLife: "", sequence: "" });
      setShowNewProd(false);
      refreshCatalog();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to add product"); }
  };

  const startEdit = (p) => {
    setEditProd(p.id);
    setProdDraft({ name: p.name, size: p.size, price: p.price, blurb: p.blurb, bestseller: !!p.bestseller, cas: p.cas, halfLife: p.halfLife, sequence: p.sequence });
  };

  const saveProduct = async (id) => {
    const d = prodDraft;
    const price = parseFloat(d.price);
    if (!d.name?.trim()) { toast.error("Name is required"); return; }
    if (isNaN(price) || price < 0) { toast.error("Enter a valid price"); return; }
    try {
      await axios.patch(`${API}/products/${id}`, { name: d.name, size: d.size, price, blurb: d.blurb, bestseller: !!d.bestseller, cas: d.cas, halfLife: d.halfLife, sequence: d.sequence }, authHeader());
      toast.success("Product updated");
      setEditProd(null);
      refreshCatalog();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to update product"); }
  };

  const deleteProduct = async (p) => {
    if (!window.confirm(`Delete ${p.name}? This removes it from the storefront.`)) return;
    try {
      await axios.delete(`${API}/products/${p.id}`, authHeader());
      toast.success(`${p.name} deleted`);
      refreshCatalog();
    } catch { toast.error("Failed to delete product"); }
  };

  const genMarketing = async () => {
    setMktBusy(true); setMktOut("");
    try {
      const { data } = await axios.post(`${API}/growth/marketing`, { kind: mktKind, topic: mktTopic.trim() }, authHeader());
      setMktOut(data.content);
    } catch (e) { toast.error(e.response?.data?.detail || "Generation failed"); }
    finally { setMktBusy(false); }
  };

  const winbackAll = async () => {
    if (!window.confirm("Send a win-back email to every open cart now? Carts nudged in the last 24h are skipped.")) return;
    setWinAllBusy(true);
    try {
      const { data } = await axios.post(`${API}/growth/winback-all`, {}, authHeader());
      setWinAllOut(data);
      toast.success(`Win-back sent to ${data.sent} cart${data.sent === 1 ? "" : "s"}${data.skipped ? ` · ${data.skipped} skipped` : ""}`);
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to send win-backs"); }
    finally { setWinAllBusy(false); }
  };

  const genWinback = async () => {
    if (!/^[^@]+@[^@]+\.[^@]+$/.test(winEmail)) { toast.error("Enter a valid customer email"); return; }
    setWinBusy(true); setWinOut(null);
    try {
      const { data } = await axios.post(`${API}/growth/winback`, { email: winEmail.trim() }, authHeader());
      setWinOut(data);
      toast[data.sent ? "success" : "warning"](data.sent ? `Win-back email sent to ${winEmail}` : "Generated (email not sent)");
    } catch (e) { toast.error(e.response?.data?.detail || "No open cart for that email"); }
    finally { setWinBusy(false); }
  };

  const toggleAutoWinback = async () => {
    const next = !autoWinback;
    setAutoWinback(next);
    try {
      await axios.patch(`${API}/growth/auto-winback`, { enabled: next }, authHeader());
      toast.success(next ? "Auto win-back ON — abandoned carts emailed after 24h" : "Auto win-back turned off");
    } catch { setAutoWinback(!next); toast.error("Failed to update setting"); }
  };

  const sendBlast = async () => {
    const code = blast.code.trim().toUpperCase();
    const existing = promos.find((p) => p.code === code);
    const percent = blast.percent === "" ? null : parseInt(blast.percent, 10);
    if (!code || /\s/.test(code)) { toast.error("Enter a promo code (no spaces)"); return; }
    if (percent !== null && !(percent >= 1 && percent <= 100)) { toast.error("Discount must be 1–100% (or leave blank to use the code's existing deal)"); return; }
    if (!existing && percent === null) { toast.error("New code — enter a discount %, or create the deal in Promos first"); return; }
    const dealText = existing && (existing.kind || "percent") !== "percent"
      ? promoLabel(existing)
      : `${percent ?? existing?.percent}% off`;
    let scheduled_at = null;
    if (blastSchedule) {
      if (!blast.scheduledAt) { toast.error("Pick a date & time to schedule"); return; }
      const when = new Date(blast.scheduledAt);
      if (isNaN(when.getTime()) || when.getTime() <= Date.now()) { toast.error("Scheduled time must be in the future"); return; }
      scheduled_at = when.toISOString();
    }
    const confirmMsg = scheduled_at
      ? `Schedule code ${code} (${dealText}) to email ${subs.length} subscriber(s) on ${new Date(scheduled_at).toLocaleString()}?`
      : `Email ${subs.length} subscriber(s) the code ${code} for ${dealText} now?`;
    if (!window.confirm(confirmMsg)) return;
    setBlastBusy(true);
    try {
      const { data } = await axios.post(`${API}/marketing/blast`,
        { code, percent, headline: blast.headline.trim() || null, message: blast.message.trim() || null, scheduled_at },
        authHeader());
      if (data.scheduled) toast.success(`Scheduled ${code} for ${new Date(data.scheduled_at).toLocaleString()} — ${data.recipients} recipient(s).`);
      else toast.success(`Blasting ${code} to ${data.queued} subscriber(s) — emails are sending now.`);
      setBlast({ code: "", percent: "", headline: "", message: "", scheduledAt: "" });
      setBlastSchedule(false);
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not send blast"); }
    finally { setBlastBusy(false); }
  };

  const cancelCampaign = async (id) => {
    if (!window.confirm("Cancel this scheduled blast?")) return;
    try {
      await axios.delete(`${API}/campaigns/${id}`, authHeader());
      toast.success("Scheduled blast cancelled");
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not cancel"); }
  };

  const approveAmbassador = async (a) => {
    const draft = ambForm[a.id] || {};
    const payload = {
      code: (draft.code || "").trim().toUpperCase() || null,
      buyer_discount: parseInt(draft.buyer_discount ?? 10, 10),
      commission_rate: parseInt(draft.commission_rate ?? 15, 10),
    };
    if (!(payload.buyer_discount >= 1 && payload.buyer_discount <= 100) || !(payload.commission_rate >= 1 && payload.commission_rate <= 100)) {
      toast.error("Discount & commission must be 1–100%"); return;
    }
    try {
      const { data } = await axios.post(`${API}/ambassadors/${a.id}/approve`, payload, authHeader());
      toast.success(`${a.name} approved — code ${data.code} (${data.buyer_discount}% off / ${data.commission_rate}% commission). Welcome email sent.`, { duration: 7000 });
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not approve"); }
  };

  const rejectAmbassador = async (a) => {
    if (!window.confirm(`Reject ${a.name}'s application?`)) return;
    try { await axios.post(`${API}/ambassadors/${a.id}/reject`, {}, authHeader()); toast.success("Application rejected"); loadAll(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not reject"); }
  };

  const deleteAmbassador = async (a) => {
    if (!window.confirm(`Delete ${a.name} from file entirely? Their referral code${a.code ? ` (${a.code})` : ""} will stop working. This cannot be undone.`)) return;
    try { await axios.delete(`${API}/ambassadors/${a.id}`, authHeader()); toast.success(`${a.name} removed from file`); loadAll(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not delete"); }
  };

  const deleteCart = async (mail) => {
    if (!window.confirm(`Delete the saved cart for ${mail}?`)) return;
    try { await axios.delete(`${API}/carts/${encodeURIComponent(mail)}`, authHeader()); toast.success("Cart deleted"); loadAll(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not delete cart"); }
  };

  const payoutAmbassador = async (a) => {
    if (!window.confirm(`Mark ${money(a.unpaid || 0)} as paid to ${a.name}?`)) return;
    const note = window.prompt("Transaction note for this payout (optional — e.g. 'USDT tx 0x…', 'Zelle 10/16'):");
    if (note === null) return; // Cancel aborts
    setPayingId(a.id);
    try { const { data } = await axios.post(`${API}/ambassadors/${a.id}/payout`, {}, { ...authHeader(), params: note ? { note } : {} }); toast.success(`Marked ${data.orders_marked} order(s) paid`); loadAll(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not record payout"); }
    finally { setPayingId(null); }
  };

  const payViaPaypal = async (a) => {
    if (!window.confirm(`Send ${money(a.unpaid || 0)} to ${a.name} via PayPal (${a.paypal_email})? This transfers real funds.`)) return;
    setPayingId(a.id);
    try {
      const { data } = await axios.post(`${API}/ambassadors/${a.id}/paypal-payout`, {}, authHeader());
      toast.success(`Sent ${money(data.amount)} to ${data.paypal_email} · ${data.orders_marked} order(s) settled`);
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "PayPal payout failed"); }
    finally { setPayingId(null); }
  };

  const issueStoreCredit = async (a) => {
    if (!window.confirm(`Issue a store-credit code worth ${money(a.unpaid || 0)} (rounded down to whole dollars) to ${a.name}? It will be emailed to them.`)) return;
    setPayingId(a.id);
    try {
      const { data } = await axios.post(`${API}/ambassadors/${a.id}/store-credit-payout`, {}, authHeader());
      toast.success(`Issued $${data.amount} store credit · code ${data.code} (emailed to ${a.email})`);
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not issue store credit"); }
    finally { setPayingId(null); }
  };

  const exportPayoutsCsv = async () => {
    try {
      const res = await axios.get(`${API}/ambassadors/payouts/export.csv`, { ...authHeader(), responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const link = document.createElement("a");
      link.href = url; link.download = "molecularix-payouts.csv";
      document.body.appendChild(link); link.click(); document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success("Payouts CSV downloaded");
    } catch (e) { toast.error("Could not export payouts"); }
  };

  const uploadCoa = async (productId, file) => {
    if (!file) return;
    if (file.type !== "application/pdf" && !file.name.toLowerCase().endsWith(".pdf")) { toast.error("COA must be a PDF"); return; }
    const fd = new FormData();
    fd.append("file", file);
    try {
      await axios.post(`${API}/products/${productId}/coa`, fd, { ...authHeader(), headers: { "Content-Type": "multipart/form-data" } });
      toast.success("COA uploaded");
      refreshCatalog();
    } catch (e) { toast.error(e.response?.data?.detail || "COA upload failed"); }
  };

  const createPromo = async () => {
    const code = newPromo.code.trim().toUpperCase();
    const kind = newPromo.kind || "percent";
    if (!code || code.includes(" ")) { toast.error("Enter a code with no spaces"); return; }
    const payload = { code, kind };
    if (kind === "percent") {
      const percent = parseInt(newPromo.percent, 10);
      if (isNaN(percent) || percent < 1 || percent > 100) { toast.error("Percent must be 1-100"); return; }
      payload.percent = percent;
    } else if (kind === "bundle") {
      const qty = parseInt(newPromo.bundle_qty, 10);
      const price = parseFloat(newPromo.bundle_price);
      if (isNaN(qty) || qty < 2) { toast.error("Bundle quantity must be at least 2"); return; }
      if (isNaN(price) || price <= 0) { toast.error("Enter the bundle price (e.g. 150)"); return; }
      payload.bundle_qty = qty; payload.bundle_price = price;
    } else if (kind === "amount") {
      const amt = parseFloat(newPromo.amount_off);
      if (isNaN(amt) || amt <= 0) { toast.error("Enter the $ amount off"); return; }
      payload.amount_off = amt;
    }
    if (newPromo.max_uses) payload.max_uses = parseInt(newPromo.max_uses, 10);
    if (newPromo.expires_at) payload.expires_at = new Date(newPromo.expires_at).toISOString();
    try {
      const { data } = await axios.post(`${API}/promo-codes`, payload, authHeader());
      toast.success(`Code ${code} created — ${data.label || ""} · cart & checkout update automatically`);
      setNewPromo({ code: "", kind: "percent", percent: "", amount_off: "", bundle_qty: "3", bundle_price: "", max_uses: "", expires_at: "" });
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to create code"); }
  };

  const updatePromo = async (code, updates) => {
    try {
      await axios.patch(`${API}/promo-codes/${code}`, updates, authHeader());
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to update"); }
  };

  const deletePromo = async (code) => {
    try {
      await axios.delete(`${API}/promo-codes/${code}`, authHeader());
      toast.success(`Deleted ${code}`);
      loadAll();
    } catch { toast.error("Failed to delete"); }
  };

  const saveDeal = async () => {
    if (!dealDraft.code) { toast.error("Choose a promo code for the deal"); return; }
    try {
      await axios.patch(`${API}/deal`, {
        code: dealDraft.code,
        headline: dealDraft.headline.trim() || "Deal of the Week",
        until: dealDraft.until ? new Date(dealDraft.until).toISOString() : null,
      }, authHeader());
      toast.success("Deal of the Week is live on the homepage");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to save deal"); }
  };

  const clearDeal = async () => {
    try {
      await axios.patch(`${API}/deal`, { code: "", headline: "", until: null }, authHeader());
      setDealDraft({ code: "", headline: "", until: "" });
      toast.success("Deal banner turned off");
    } catch { toast.error("Failed to clear deal"); }
  };

  const nudge = async (mail, force = false) => {
    try {
      await axios.post(`${API}/carts/nudge`, { email: mail, force }, authHeader());
      toast.success(`Reminder sent to ${mail}`);
      setCarts((prev) => prev.map((c) => (c.email === mail ? { ...c, nudged: true } : c)));
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to send reminder"); }
  };

  // XSS-safe label printing: build the popup DOM imperatively instead of document.write.
  const printFallbackLabel = (o) => {
    const w = window.open("", "_blank");
    if (!w) { toast.error("Allow pop-ups to print labels"); return; }
    const s = o.shipping || {};
    w.document.title = o.order_ref || "Shipping Label";
    const style = w.document.createElement("style");
    style.textContent = "@page{size:4in 6in;margin:0.15in}body{font-family:Arial,Helvetica,sans-serif;margin:0;color:#000;background:#fff}.lbl{width:3.7in;padding:4px}.mono{font-family:'Courier New',monospace}.hr{border-top:2.5px solid #000;margin:7px 0}.sm{font-size:10px}.md{font-size:12px}.lg{font-size:15px;font-weight:700}.xl{font-size:21px;font-weight:800;letter-spacing:0.3px}";
    w.document.head.appendChild(style);
    const root = w.document.createElement("div");
    root.className = "lbl";
    const add = (cls, text) => {
      if (!text) return;
      const d = w.document.createElement("div");
      d.className = cls; d.textContent = text;
      root.appendChild(d);
    };
    const hr = () => { const d = w.document.createElement("div"); d.className = "hr"; root.appendChild(d); };
    add("sm mono", "FROM: MOLECULARIX BIOLABS · 205 Carpenter Street, Muncy, PA 17766");
    add("sm mono", `ORDER ${o.order_ref || ""} · ${new Date(o.created_at || Date.now()).toLocaleDateString()}`);
    hr();
    add("sm", "SHIP TO:");
    add("xl", s.name || o.email || "Customer");
    add("lg", [s.line1, s.line2].filter(Boolean).join(", "));
    add("lg", `${[s.city, s.state].filter(Boolean).join(", ")} ${s.zip || ""}`.trim());
    if (s.phone) add("md mono", `Tel: ${s.phone}`);
    hr();
    add("sm mono", "CONTENTS (RESEARCH MATERIALS):");
    (o.items || []).forEach((i) => add("sm mono", `${i.qty}× ${i.name}${i.size ? ` (${i.size})` : ""}`));
    hr();
    add("sm", "For laboratory research use only. Not for human consumption.");
    w.document.body.appendChild(root);
    setTimeout(() => w.print(), 300);
  };

  const printLabel = async (o) => {
    try {
      const { data } = await axios.get(`${API}/orders/${o.id}/label`, authHeader());
      const w = window.open("", "_blank");
      if (w) {
        w.document.title = data.order_ref;
        w.document.body.style.margin = "0";
        w.document.body.style.textAlign = "center";
        const img = w.document.createElement("img");
        img.src = data.image_base64;
        img.style.maxWidth = "100%";
        img.onload = () => w.print();
        w.document.body.appendChild(img);
      }
    } catch (e) {
      // No carrier/stored label — print a standardized 4x6 thermal address label instead.
      printFallbackLabel(o);
    }
  };

  const saveTracking = async (o) => {
    const d = trackDraft[o.id] || {};
    try {
      await axios.patch(`${API}/orders/${o.id}/tracking`, { carrier: d.carrier || null, number: d.number || null, url: d.url || null }, authHeader());
      setOrders((prev) => prev.map((x) => (x.id === o.id ? { ...x, tracking: { carrier: d.carrier, number: d.number, url: d.url } } : x)));
      toast.success("Tracking saved" + (o.status === "shipped" ? " — customer emailed" : ""));
    } catch { toast.error("Failed to save tracking"); }
  };

  const sendDigest = async () => {
    try {
      const { data } = await axios.post(`${API}/digest/send`, {}, authHeader());
      toast.success(`Digest emailed — ${data.orders} orders, $${data.revenue}`);
      loadAll();
    } catch { toast.error("Failed to send digest"); }
  };

  const saveDigestHour = async (h) => {
    setDigestHour(h);
    try { await axios.patch(`${API}/settings`, { digest_hour: h }, authHeader()); toast.success(`Digest hour set to ${h}:00 UTC`); }
    catch { toast.error("Failed to save"); }
  };

  const runBackup = async () => {
    try {
      const { data } = await axios.post(`${API}/backups/run`, {}, authHeader());
      toast.success(`Backup created & emailed — ${data.filename}`);
      loadAll();
    } catch { toast.error("Backup failed"); }
  };

  const downloadBackup = async (name) => {
    try {
      const res = await axios.get(`${API}/backups/${name}`, { ...authHeader(), responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = name; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };

  const exportCsv = () => {
    const rows = [["Email", "Source", "Welcome Code", "Redeemed", "Date"],
      ...subs.map((sub) => [sub.email, sub.source, sub.welcome_code || "", sub.welcome_code_used ? "yes" : "no", new Date(sub.created_at).toISOString()])];
    const csv = rows.map((r) => r.map((c) => `"${c}"`).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url; a.download = `molecularix-subscribers-${Date.now()}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const downloadSalesCsv = async (start, end, all = false, accounting = false) => {
    try {
      const params = new URLSearchParams();
      if (start) params.set("start", start);
      if (end) params.set("end", end);
      if (all) params.set("all", "true");
      if (accounting) params.set("fmt", "accounting");
      const qs = params.toString();
      const res = await axios.get(`${API}/orders/export.csv${qs ? `?${qs}` : ""}`, { ...authHeader(), responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      const span = start || end ? `${start || "start"}_to_${end || "now"}` : new Date().toISOString().slice(0, 10);
      const kind = accounting ? "accounting" : all ? "all-orders" : "sales";
      a.download = `molecularix-${kind}-${span}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(accounting ? "Accounting CSV exported" : all ? "All orders exported" : "Sales log exported");
    } catch { toast.error("Export failed"); }
  };

  return {
    installApp, enablePush, disablePush, saveGoal, resetData, login, updateStatus, notifyRestock,
    toggleStock, saveQuantity, saveCost, createProduct, startEdit, saveProduct, deleteProduct,
    genMarketing, winbackAll, genWinback, toggleAutoWinback, sendBlast, cancelCampaign,
    approveAmbassador, rejectAmbassador, deleteAmbassador, deleteCart, payoutAmbassador,
    payViaPaypal, issueStoreCredit, exportPayoutsCsv, uploadCoa, createPromo, updatePromo,
    deletePromo, saveDeal, clearDeal, nudge, printLabel, saveTracking, sendDigest, saveDigestHour,
    runBackup, downloadBackup, exportCsv, downloadSalesCsv, loadAll,
  };
}

export { authHeader };
